import {
  subscribeReaders,
  subscribeSessions,
  subscribeToAck,
  sendCommand as sendCommandDirect,
  publishAppPresence,
  setExpectedOwner,
  type AckState,
  type BotPresence,
  type ExtensionPresence,
  type SessionState,
  type SyncCommandInput
} from './client';
import { getOrCreateDeviceId, getClientContext } from './device';
import type { TransportHandlers, RealtimeTransport } from './transport';

// Leader-elected RTDB transport (see refs/realtime-sync "Connection management").
//
// Problem: embeds (PowerPoint, Gamma) render the StreamAlive app once per slide,
// each in its own iframe/JS realm. Naively, every realm opens its own RTDB
// socket + listeners. This module collapses all same-origin frames/tabs to a
// SINGLE socket: exactly one frame ("leader") holds the RTDB subscriptions and
// publishes presence; the rest ("followers") receive state over a
// BroadcastChannel and forward commands to the leader.
//
// Leadership uses the Web Locks API, which auto-releases when the holding frame
// is destroyed — important for Gamma, which removes offscreen slide iframes and
// re-adds them on scroll. Only *visible* frames contend, so leadership prefers a
// live, on-screen frame and steps down when its frame is hidden/torn down.

const CHANNEL_NAME = 'sa-rtdb';
const LOCK_NAME = 'sa-rtdb-leader';
const COMMAND_ACK_TIMEOUT_MS = 500;
// Grace window between winning the lock and actually opening the socket. In
// Gamma, leadership hops across slide iframes as the user scrolls; waiting
// briefly means a frame the user scrolls straight past never opens a connection.
const LEADER_GRACE_MS = 2500;

type ChannelMessage =
  | { type: 'presence'; data: ExtensionPresence }
  | { type: 'bots'; data: Record<string, BotPresence> }
  | { type: 'sessions'; data: Record<string, SessionState> }
  | { type: 'command'; id: number; input: SyncCommandInput }
  | { type: 'commandAck'; id: number }
  | { type: 'watchAck'; id: number; commandId: string }
  | { type: 'watchAckAck'; id: number }
  | { type: 'unwatchAck'; commandId: string }
  | { type: 'ack'; commandId: string; data: AckState | null }
  | { type: 'requestState' };

export function startLeaderTransport(
  handlers: TransportHandlers,
  ownerKey: string | null = null
): RealtimeTransport {
  // Invalidate a sign-in cached for a *different* owner (team switch) before any
  // subscription or command in this transport touches RTDB.
  setExpectedOwner(ownerKey);

  const hasWindow = typeof window !== 'undefined';
  const hasChannel = hasWindow && typeof BroadcastChannel !== 'undefined';
  const hasLocks = hasWindow && typeof navigator !== 'undefined' && !!navigator.locks;

  let stopped = false;
  let isLeader = false;
  let presenceUserId: string | null = null;

  // Latest snapshots, cached so the leader can answer a follower's `requestState`.
  let lastPresence: ExtensionPresence | null = null;
  let lastBots: Record<string, BotPresence> | null = null;
  let lastSessions: Record<string, SessionState> | null = null;

  let unsubReaders: (() => void) | null = null;
  let unsubSessions: (() => void) | null = null;

  const channel = hasChannel ? new BroadcastChannel(CHANNEL_NAME) : null;
  const post = (msg: ChannelMessage) => channel?.postMessage(msg);

  const publishPresenceIfLeader = () => {
    if (isLeader && presenceUserId) {
      publishAppPresence(presenceUserId, getOrCreateDeviceId(), getClientContext());
    }
  };

  // --- Leadership: hold the RTDB subscriptions for the whole browser ---------

  const becomeLeader = () => {
    if (isLeader || stopped) return;
    isLeader = true;
    unsubReaders = subscribeReaders(({ presence, bots }) => {
      lastPresence = presence;
      lastBots = bots;
      handlers.onPresence(presence);
      handlers.onBots(bots);
      post({ type: 'presence', data: presence });
      post({ type: 'bots', data: bots });
    });
    unsubSessions = subscribeSessions((sessions) => {
      lastSessions = sessions;
      handlers.onSessions(sessions);
      post({ type: 'sessions', data: sessions });
    });
    publishPresenceIfLeader();
  };

  const stepDown = () => {
    isLeader = false;
    unsubReaders?.();
    unsubReaders = null;
    unsubSessions?.();
    unsubSessions = null;
  };

  // Web Locks contention. We hold the lock by keeping the request callback's
  // promise unresolved; resolving it (releaseHeld) releases leadership so the
  // next waiter takes over.
  let abortController: AbortController | null = null;
  let releaseHeld: (() => void) | null = null;
  let graceTimer: ReturnType<typeof setTimeout> | null = null;

  const contend = () => {
    if (stopped || !hasLocks || isLeader || abortController) return;
    if (document.visibilityState !== 'visible') return;

    const ac = new AbortController();
    abortController = ac;
    navigator.locks
      .request(LOCK_NAME, { mode: 'exclusive', signal: ac.signal }, () =>
        new Promise<void>((resolve) => {
          releaseHeld = resolve;
          // Hold the lock but defer opening the socket: if we're relinquished
          // during the grace window (frame scrolled away/hidden) the timer is
          // cleared and no connection is ever opened for this transient frame.
          graceTimer = setTimeout(() => {
            graceTimer = null;
            becomeLeader();
          }, LEADER_GRACE_MS);
        })
      )
      .catch(() => {
        // Aborted before/while acquiring (frame hidden or stopped) — not an error.
      })
      .finally(() => {
        if (abortController === ac) abortController = null;
      });
  };

  // Give up leadership (if held) or stop waiting for it (if contending).
  const relinquish = () => {
    if (graceTimer) {
      clearTimeout(graceTimer);
      graceTimer = null;
    }
    if (releaseHeld) {
      const release = releaseHeld;
      releaseHeld = null;
      stepDown();
      release();
    } else if (abortController) {
      abortController.abort();
      abortController = null;
    }
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') contend();
    else relinquish();
  };

  // --- Commands: route through the leader, fall back to a direct write -------

  let commandSeq = 0;
  const pendingAcks = new Map<number, () => void>();

  const sendCommand = async (input: SyncCommandInput): Promise<void> => {
    if (isLeader || !channel) {
      await sendCommandDirect(input);
      return;
    }
    const id = ++commandSeq;
    const acked = new Promise<boolean>((resolve) => {
      pendingAcks.set(id, () => resolve(true));
      setTimeout(() => {
        if (pendingAcks.delete(id)) resolve(false);
      }, COMMAND_ACK_TIMEOUT_MS);
    });
    post({ type: 'command', id, input });
    // No leader answered (e.g. mid-failover): write directly to avoid losing the
    // user's action. Duplicates are harmless — start/stop reading are idempotent.
    if (!(await acked)) await sendCommandDirect(input);
  };

  // --- Ack watches: same shape as commands (leader owns the listener, followers
  // ride the BroadcastChannel, direct fallback if no leader answers) ----------

  // This frame's local watchers, keyed by commandId.
  const ackWatchers = new Map<string, Set<(ack: AckState | null) => void>>();
  // Listeners this frame opened directly on RTDB (leader, or a follower whose
  // watch request no leader answered), so they can be detached.
  const directAckUnsubs = new Map<string, () => void>();
  // Leader-side listeners held on behalf of followers, ref-counted per commandId.
  // Deliberately NOT torn down in stepDown, unlike the readers/sessions
  // subscriptions: those are long-lived snapshots worth moving to the new leader,
  // whereas an ack watch is a handful of seconds on a tiny node, and dropping it
  // mid-flight would strand the follower waiting on it (a new leader gets no
  // re-registration — followers only post watchAck once). They're released on
  // unwatchAck, or with the transport in stop().
  const relayedAckSubs = new Map<string, { unsub: () => void; refs: number }>();

  const emitAck = (commandId: string, data: AckState | null) => {
    ackWatchers.get(commandId)?.forEach((fn) => fn(data));
  };

  const subscribeAckDirect = (commandId: string) => {
    if (directAckUnsubs.has(commandId)) return;
    directAckUnsubs.set(commandId, subscribeToAck(commandId, (ack) => emitAck(commandId, ack)));
  };

  const watchAck = (commandId: string, onAck: (ack: AckState | null) => void) => {
    const listeners = ackWatchers.get(commandId) ?? new Set<(ack: AckState | null) => void>();
    listeners.add(onAck);
    ackWatchers.set(commandId, listeners);

    if (isLeader || !channel) {
      subscribeAckDirect(commandId);
    } else {
      const id = ++commandSeq;
      const acked = new Promise<boolean>((resolve) => {
        pendingAcks.set(id, () => resolve(true));
        setTimeout(() => {
          if (pendingAcks.delete(id)) resolve(false);
        }, COMMAND_ACK_TIMEOUT_MS);
      });
      post({ type: 'watchAck', id, commandId });
      // No leader answered (mid-failover, or leadership hasn't been won yet):
      // open the listener here rather than silently never reporting an outcome.
      void acked.then((ok) => {
        if (!ok && ackWatchers.has(commandId)) subscribeAckDirect(commandId);
      });
    }

    return () => {
      const current = ackWatchers.get(commandId);
      if (!current) return;
      current.delete(onAck);
      if (current.size) return;
      ackWatchers.delete(commandId);
      directAckUnsubs.get(commandId)?.();
      directAckUnsubs.delete(commandId);
      if (!isLeader && channel) post({ type: 'unwatchAck', commandId });
    };
  };

  if (channel) {
    channel.onmessage = (event: MessageEvent<ChannelMessage>) => {
      const msg = event.data;
      if (!msg) return;
      switch (msg.type) {
        case 'presence':
          if (!isLeader) {
            lastPresence = msg.data;
            handlers.onPresence(msg.data);
          }
          break;
        case 'bots':
          if (!isLeader) {
            lastBots = msg.data;
            handlers.onBots(msg.data);
          }
          break;
        case 'sessions':
          if (!isLeader) {
            lastSessions = msg.data;
            handlers.onSessions(msg.data);
          }
          break;
        case 'command':
          if (isLeader) {
            sendCommandDirect(msg.input)
              .catch(() => {})
              .finally(() => post({ type: 'commandAck', id: msg.id }));
          }
          break;
        case 'commandAck':
        case 'watchAckAck': {
          const ack = pendingAcks.get(msg.id);
          if (ack) {
            pendingAcks.delete(msg.id);
            ack();
          }
          break;
        }
        case 'watchAck':
          if (isLeader) {
            const existing = relayedAckSubs.get(msg.commandId);
            if (existing) existing.refs += 1;
            else {
              relayedAckSubs.set(msg.commandId, {
                refs: 1,
                unsub: subscribeToAck(msg.commandId, (ack) => {
                  emitAck(msg.commandId, ack);
                  post({ type: 'ack', commandId: msg.commandId, data: ack });
                })
              });
            }
            post({ type: 'watchAckAck', id: msg.id });
          }
          break;
        case 'unwatchAck':
          if (isLeader) {
            const entry = relayedAckSubs.get(msg.commandId);
            if (entry) {
              entry.refs -= 1;
              if (entry.refs <= 0) {
                relayedAckSubs.delete(msg.commandId);
                entry.unsub();
              }
            }
          }
          break;
        case 'ack':
          if (!isLeader) emitAck(msg.commandId, msg.data);
          break;
        case 'requestState':
          if (isLeader) {
            if (lastPresence) post({ type: 'presence', data: lastPresence });
            if (lastBots) post({ type: 'bots', data: lastBots });
            if (lastSessions) post({ type: 'sessions', data: lastSessions });
          }
          break;
        default:
          break;
      }
    };
  }

  // --- Bootstrap -------------------------------------------------------------

  if (!hasLocks) {
    // No Web Locks (SSR / old browser): degrade to one socket per realm, i.e. the
    // pre-leader behaviour. This frame owns its own subscriptions.
    becomeLeader();
  } else {
    document.addEventListener('visibilitychange', onVisibilityChange);
    contend();
    // Pull current state from whichever frame is already leader, so a freshly
    // (re)added slide iframe renders live data without waiting for the next push.
    post({ type: 'requestState' });
  }

  return {
    sendCommand,
    watchAck,
    setPresenceIdentity: (userId: string | null) => {
      presenceUserId = userId;
      publishPresenceIfLeader();
    },
    stop: () => {
      stopped = true;
      if (hasLocks) document.removeEventListener('visibilitychange', onVisibilityChange);
      relinquish();
      stepDown();
      pendingAcks.clear();
      ackWatchers.clear();
      directAckUnsubs.forEach((unsub) => unsub());
      directAckUnsubs.clear();
      relayedAckSubs.forEach((entry) => entry.unsub());
      relayedAckSubs.clear();
      channel?.close();
    }
  };
}
